import { useTranslations } from "next-intl";
import { Card } from "@/components/ui";
import type { InstructorDashboardSummary } from "@/services/api/instructor";

type SummaryState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; summary: InstructorDashboardSummary };

type InstructorFiguresProps = {
  publishedCourses: number;
  summaryState: SummaryState;
};

/**
 * CRS-008: the counts an instructor acts on. A count that has not arrived shows
 * the loading word, and one that failed shows a dash — never a 0 that reads as
 * "nothing to do".
 */
export function InstructorFigures({ publishedCourses, summaryState }: InstructorFiguresProps) {
  const t = useTranslations("instructor");

  function pending(value: (summary: InstructorDashboardSummary) => number) {
    if (summaryState.status === "ready") {
      return { text: String(value(summaryState.summary)), muted: false };
    }
    if (summaryState.status === "loading") return { text: t("loading"), muted: true };
    return { text: "—", muted: true };
  }

  const figures = [
    {
      id: "students",
      label: t("figureStudents"),
      ...pending((summary) => summary.studentsEnrolled),
    },
    { id: "published", label: t("figurePublished"), text: String(publishedCourses), muted: false },
    {
      id: "awaiting",
      label: t("figureAwaiting"),
      ...pending((summary) => summary.submissionsAwaitingMarking),
    },
  ];

  return (
    <section aria-label={t("figuresLabel")} data-testid="instructor-figures">
      <dl
        className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4"
        aria-busy={summaryState.status === "loading"}
      >
        {figures.map((figure) => (
          <Card key={figure.id} pad="sm" data-testid={`instructor-figure-${figure.id}`}>
            <dt className="text-sm text-[var(--text-muted)]">{figure.label}</dt>
            <dd
              className={
                figure.muted
                  ? "mt-2 text-base text-[var(--text-muted)]"
                  : "mt-1 text-3xl font-semibold tabular-nums text-[var(--text-heading)]"
              }
              data-testid={`instructor-figure-${figure.id}-value`}
            >
              {figure.text}
            </dd>
          </Card>
        ))}
      </dl>
      {summaryState.status === "error" ? (
        <p
          role="status"
          className="mt-2 text-sm text-[var(--text-muted)]"
          data-testid="instructor-figures-error"
        >
          {t("figuresLoadFailed")}
        </p>
      ) : null}
    </section>
  );
}
